import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { searchBoxRetrieve, searchBoxSuggest } from 'src/api/commonApi';
import { formatPlaceMedium } from 'src/utils/formatPlaceName';
import { getLocationIcon } from './locationIcons';
import { reportLocationRetrieveFailure } from './reportLocationRetrieveFailure';
import { track } from 'src/analytics/mixpanel';
import { AnalyticsEvents } from 'src/analytics/events';
import { useMarket } from 'src/hooks/useMarket';
import { useSearchProximity } from 'src/hooks/useSearchProximity';

type LocationFilterProps = {
   setLocation: any;
   searchText: string;
   locations: any;
   setLocations: any;
   styles?: string;
   siteId?: string;
   country?: string;
   className?: string;
   proximity?: string;
   includeLodging?: boolean;
};

const LocationFilter = (props: LocationFilterProps) => {
   const { searchText, setLocation, locations, setLocations, styles, siteId, country, className, proximity: proximityProp, includeLodging = false } = props;
   const [t] = useTranslation();
   const [value, setValue]: any = useState('');
   // NPT-2053 — list places in the market's countries first (org market, else the site's).
   const biasCountries = useMarket().searchBiasCountries.join(',');
   // NPT-2053 — rank nearby places first. A hard country filter means the traveler is typing their
   // own address, so only their own position counts; a destination search anchors on the org.
   const autoProximity = useSearchProximity(country ? 'address' : 'destination');
   const proximity = proximityProp ?? autoProximity;

   const handlegSearchBoxSuggest = () =>
      value ? searchBoxSuggest(value, 10, siteId, country, proximity, includeLodging, biasCountries.split(',')) : null;
   const { data: dataGeocoding, isLoading } = useQuery({
      queryKey: ['searchBoxSuggest', value, siteId, country, proximity, includeLodging, biasCountries],
      queryFn: handlegSearchBoxSuggest,
      staleTime: Infinity,
   });

   // An answered query replaces the list even when it is empty (the API returns {} on
   // failure), so suggestions for an earlier prefix don't linger as if they matched.
   useEffect(() => {
      if (dataGeocoding && value) {
         const results = Array.isArray(dataGeocoding?.data) ? dataGeocoding.data : [];
         setLocations?.(results);
         if (results.length === 0 && value.trim().length >= 4) {
            track(AnalyticsEvents.LOCATION_SUGGEST_NO_RESULTS, { searchText: value, siteId, country });
         }
      }
   }, [dataGeocoding, value]);

   useEffect(() => {
      if (!isLoading) {
         setValue(searchText);
         if (!searchText) {
            setLocations?.([]);
         }
      }
   }, [isLoading, searchText]);

   // `mutate`, not `mutateAsync`: onError reports the failure, and nothing awaits the promise, so a
   // rejected one would surface as an unhandled rejection on top of it.
   const { mutate: retrieveLocation } = useMutation({
      mutationKey: ['searchBoxRetrieve'],
      mutationFn: ({ mapboxId, siteId }: { mapboxId: string; siteId?: string }) => searchBoxRetrieve(mapboxId, siteId),
      onError: (error, { mapboxId }) => reportLocationRetrieveFailure('request_failed', t('homeFilter.location.retrieveError'), mapboxId, error),
      onSuccess: ({ data }, { mapboxId: requestedId }) => {
         if (!data?.coordinates || data.coordinates.longitude == null || data.coordinates.latitude == null) {
            reportLocationRetrieveFailure('no_coordinates', t('homeFilter.location.retrieveError'), requestedId);
            return;
         }
         const center = [data.coordinates.longitude, data.coordinates.latitude];
         const place_name = data?.overrideName || formatPlaceMedium(data);
         const mapboxId = data.mapbox_id;
         setLocation({ place_name, center, context: data.context, mapboxId, mapboxObject: data });
      },
   });

   const selectLocation = (location: any) => {
      if (location?.coordinates?.longitude && location?.coordinates?.latitude) {
         const center = [location.coordinates.longitude, location.coordinates.latitude];
         const place_name = location?.overrideName || formatPlaceMedium(location);
         const mapboxId = location.mapboxId || location.mapbox_id;
         setLocation({ place_name, center, context: location.context, mapboxId, mapboxObject: location });
      } else {
         retrieveLocation({ mapboxId: location?.mapboxId || location?.mapbox_id, siteId });
      }
   };

   return (
      <div className={`flex-1 w-full sm:min-w-[350px] py-2 ${className ? className : 'max-h-[400px]'} overflow-y-auto ${styles}`}>
         {locations?.map((sugg: any, idx: number) => {
            const title = sugg?.overrideName || sugg.name;
            const note = sugg.place_formatted;
            const Icon = getLocationIcon(sugg);
            return (
               <button
                  type="button"
                  className="w-full min-h-[55px] h-full px-3.75 flex items-center py-2 hover:bg-orgLightPrimary"
                  key={idx}
                  onMouseDown={() => selectLocation(sugg)}
               >
                  <div className="flex gap-2.5 h-full">
                     <div>
                        <Icon className="text-twGray" width={16} height={16} />
                     </div>
                     <div className="text-start flex flex-col gap-1.25">
                        <div className="text-l-sm font-bold">{title}</div>
                        <div className="text-l-xs">{note}</div>
                     </div>
                  </div>
               </button>
            );
         })}
      </div>
   );
};
export default LocationFilter;
